import React, { useMemo } from 'react';
import { IconChevronRight, IconSparkles, IconPlayerPlay, IconVideo } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import TopicEditModal from './TopicEditModal';
import { TopicGroup } from './types';
import { getClipDurationInSeconds } from '@/player-config/src/utils/clipUtils';
import { formatTime } from '@/libs/core';
import { Clip } from '@/player-config/src/types/clip-types';
import IconButton from '@/components/atoms/Button/IconButton';
import { updateClipTopic, handleRefreshTopic } from '@/stores/clipRecommendations';
import useDialog from '@/components/organisms/useDialog';
import useAnalytics from '@/hooks/useAnalytics';
import TooltipV2 from '@/components/TooltipV2';
import HomePageClipThumbnail from '@/Pages/ClipsListPage/HomePageClipThumbnail';

interface ClipSectionProps {
  topicGroup: TopicGroup;
  onClipClick: (clip: Clip) => void;
  topicIndex: number;
  hasLimitedContent?: boolean;
}

const ClipSection: React.FC<ClipSectionProps> = ({
  topicGroup,
  onClipClick,
  topicIndex,
  hasLimitedContent = false
}) => {
  const { isOpen: isEditModalOpen, openDialog: openEditModal, closeDialog: closeEditModal } = useDialog();
  const navigate = useNavigate();
  const { trackClipRecommendationEvent } = useAnalytics();

  const savedSearchId = useMemo(() => topicGroup?.results?.[0]?.saved_search?.id || '', [topicGroup?.results]);
  const workspaceId = useMemo(() => topicGroup?.results?.[0]?.saved_search?.workspace_id || '', [topicGroup?.results]);

  const handleRenameTopic = (newTopic: string, newSearchTerm: string) => {
    if (!newTopic || !newSearchTerm) {
      return;
    }
    trackClipRecommendationEvent('CarouselEdit', { newTopicName: newSearchTerm, savedSearchId });

    updateClipTopic(topicIndex, newTopic, newSearchTerm, workspaceId);
    closeEditModal();
  };

  const onRefreshTopic = () => {
    const topicName = topicGroup?.topic || '';
    trackClipRecommendationEvent('CarouselRefresh', { topicName, savedSearchId });

    handleRefreshTopic(topicIndex);
  };

  const handleTopicClick = () => {
    if (topicGroup?.isLoading) {
      return;
    }

    const topicName = topicGroup?.topic || '';
    trackClipRecommendationEvent('CarouselClickToOpen', { topicName, savedSearchId });

    if (savedSearchId) {
      navigate(`/search/${savedSearchId}/clips`);
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between pr-4">
        <div className="flex cursor-pointer items-center gap-1 font-medium text-slate-950" onClick={handleTopicClick}>
          <span>{topicGroup?.title || topicGroup?.topic || 'Untitled Topic'}</span>
          <IconChevronRight size={20} />
        </div>

        <div className="flex items-center gap-2">
          <TooltipV2
            tooltipId={`edit-topic-tooltip-${topicIndex}`}
            tooltipElement={
              <IconButton
                icon="IconPencil"
                onClick={openEditModal}
                trackingId="edit-topic-button"
                variation="outline"
                buttonClassName="rounded-full size-8"
                iconClassName="size-5"
                disabled={topicGroup?.isLoading}
                title="Edit topic"
              />
            }
            tooltipContent={hasLimitedContent ? 'Upload more videos to generate more themes and clips' : ''}
            place="top"
            offset={10}
            colorTheme="light"
            size="xsmall"
            className="!max-w-[220px] !rounded-lg !px-3 !py-2 text-center !font-medium !shadow-lg"
          />
          <IconButton
            icon="IconRefresh"
            onClick={onRefreshTopic}
            trackingId="refresh-topic-button"
            variation="outline"
            buttonClassName="rounded-full size-8"
            iconClassName="size-5"
            disabled={topicGroup?.isLoading || hasLimitedContent}
            title="Refresh topic"
          />
        </div>
      </div>

      {topicGroup?.isLoading ? (
        <div className="mr-4 flex h-[242px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300">
          <IconSparkles size={20} className="text-slate-500" />
          <p className="text-slate-500">Analyzing your library to create clips. This may take few minutes.</p>
        </div>
      ) : !topicGroup?.results || topicGroup.results.length === 0 ? (
        <div className="mr-4 flex h-[242px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 px-[360px]">
          <IconVideo size={20} className="text-slate-900" />
          <div className="mb-4 text-center text-slate-900">
            <p className="font-medium">No videos found on the topic</p>
            <p className="mt-2 text-sm">
              Whoops! We couldn't find videos on "{topicGroup?.topic || 'this topic'}" to curate clips for you. You can
              try a different topic or upload more videos and try again.
            </p>
          </div>
          <IconButton
            icon="IconPencil"
            onClick={openEditModal}
            trackingId="edit-topic-button"
            variation="outline"
            buttonClassName="rounded-full bg-slate-100 text-xs"
            iconClassName="size-4"
            disabled={topicGroup?.isLoading}
            title="Edit topic"
            content="Edit topic"
          />
        </div>
      ) : (
        <div className="no-scrollbar flex gap-6 overflow-x-auto py-2">
          {topicGroup.results
            .filter(clip => clip && clip.id)
            .map(clip => (
              <div
                key={clip.id}
                className="h-[235px] shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-[250ms] ease-in-out hover:scale-[1.0075] hover:shadow-md"
                style={{ maxWidth: '298px' }}
              >
                <div
                  className="relative h-[168px] cursor-pointer"
                  onClick={() => {
                    if (clip) {
                      onClipClick(clip);
                    }
                  }}
                >
                  <div className="group flex aspect-video h-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                    <HomePageClipThumbnail clip={clip} isClipActive={false} isClipUnseen={false} layout="fullWidth" />

                    {/* Play icon overlay on hover */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-all group-hover:bg-black/50">
                      <div className="rounded-full bg-white/30 p-2 opacity-0 transition-opacity group-hover:opacity-100">
                        <IconPlayerPlay size={20} className="text-white" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  {clip?.asset_metadata && (
                    <div className="absolute bottom-2 right-2 rounded bg-slate-950 px-2 py-1 text-2xs text-white">
                      {formatTime(getClipDurationInSeconds(clip as Clip))}
                    </div>
                  )}
                </div>

                <div className="p-3">
                  <div className="mb-[6px] truncate text-sm font-medium text-slate-950">
                    {clip?.title || 'Untitled Clip'}
                  </div>
                  <p className="flex items-center gap-1 text-slate-500">
                    <IconVideo size={12} className="shrink-0" />
                    <span className="truncate text-xs">{clip?.content?.title || 'Untitled Content'}</span>
                  </p>
                </div>
              </div>
            ))}
        </div>
      )}

      <TopicEditModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        currentTopic={topicGroup?.title || topicGroup?.topic || ''}
        currentSearchTerm={topicGroup?.searchTerm || ''}
        clips={topicGroup?.results || []}
        onSave={handleRenameTopic}
        isLoading={topicGroup?.isLoading || false}
      />
    </div>
  );
};

export default ClipSection;
